import cn from 'classnames';
import {
  Dispatch,
  Fragment,
  ReactElement,
  ReactNode,
  SetStateAction,
  useEffect,
  useRef
} from 'react';
import {
  ColorKey,
  KeyShape,
  KeyShapeOptions
} from '../../../Components/Charts/ChartTooltip/ChartTooltipV2';
import { Brand100, Brand25, Brand50, Brand70, Neutral400, Neutral500 } from '../../../utils/colors';
import { formatRawBudget } from '../forecasting-constants';

interface ForecastResultsLegendRow {
  keyColor: string;
  keyShape: KeyShape;
  label: string;
  budget?: number;
}

interface ForecastResultsLegendSection {
  sectionTitle: string;
  rows: ForecastResultsLegendRow[];
}

interface ForecastResultsLegendProps {
  sections: ForecastResultsLegendSection[];
  hoveredBudget?: number | undefined;
  setHoveredBudget?: Dispatch<SetStateAction<number | undefined>>;
  selectedBudget?: number | undefined;
  setSelectedBudget?: Dispatch<SetStateAction<number | undefined>>;
  includeCiIfApplicable?: boolean;
}

const ForecastResultsLegend = ({
  sections,
  hoveredBudget,
  setHoveredBudget,
  selectedBudget,
  setSelectedBudget,
  includeCiIfApplicable = false
}: ForecastResultsLegendProps): ReactElement => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) && setSelectedBudget) {
        setSelectedBudget(undefined);
      }
    };
    if (selectedBudget !== undefined) {
      document.addEventListener('mousedown', onClickOutside);
    }
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [selectedBudget, setSelectedBudget]);

  return (
    <div className='legend' ref={ref}>
      {sections.map(({ sectionTitle, rows }) => (
        <div className='legend-section' key={sectionTitle}>
          <div className='legend-section-title'>{sectionTitle}</div>
          {rows.map(({ keyColor, keyShape, label, budget }) => {
            const isHovered = hoveredBudget !== undefined && hoveredBudget === budget;
            const isShadowed = hoveredBudget !== undefined && hoveredBudget !== budget;
            const isSelected = selectedBudget !== undefined && selectedBudget === budget;
            const showConfidenceInterval = includeCiIfApplicable && (isHovered || isSelected);
            const onMouseEnter =
              setHoveredBudget && selectedBudget === undefined
                ? () => setHoveredBudget(budget)
                : undefined;
            const onMouseLeave =
              setHoveredBudget && selectedBudget === undefined
                ? () => setHoveredBudget(undefined)
                : undefined;
            const onClick = setSelectedBudget
              ? () => {
                  setSelectedBudget(budget);
                  if (setHoveredBudget) {
                    setHoveredBudget(undefined);
                  }
                }
              : undefined;

            return (
              <Fragment key={`${sectionTitle}-${budget ?? label}`}>
                <div
                  role='presentation'
                  className={cn('legend-item', {
                    'shadowed-legend-item': isShadowed,
                    'has-interaction': Boolean(setHoveredBudget)
                  })}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                  onClick={onClick}
                >
                  <ColorKey shape={keyShape} color={keyColor} />
                  <div className='legend-item-label'>{label}</div>
                </div>
                {showConfidenceInterval && (
                  <div
                    role='presentation'
                    className='legend-item has-interaction confidence-interval'
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onClick={onClick}
                  >
                    <ColorKey shape={KeyShapeOptions.SQUARE} color={keyColor} opacity={0.25} />
                    <div className='legend-item-label'>Confidence Interval</div>
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export const buildKpiPrimaryOnlyLegend = (title: string): ReactNode => (
  <ForecastResultsLegend
    sections={[
      {
        sectionTitle: title,
        rows: [
          {
            keyColor: Brand25,
            keyShape: KeyShapeOptions.LINE,
            label: 'Historical'
          },
          {
            keyColor: Brand70,
            keyShape: KeyShapeOptions.DASHED_LINE,
            label: 'Forecasted'
          },
          {
            keyColor: Brand25,
            keyShape: KeyShapeOptions.SQUARE,
            label: 'Confidence Interval'
          }
        ]
      }
    ]}
  />
);

export const spendPrimaryOnlyLegend = (
  <ForecastResultsLegend
    sections={[
      {
        sectionTitle: 'Spend',
        rows: [
          {
            keyColor: Brand25,
            keyShape: KeyShapeOptions.SQUARE,
            label: 'Historical'
          },
          {
            keyColor: `url(#shadedStripeNeg${Brand50})`,
            keyShape: KeyShapeOptions.FILLED_RECT,
            label: 'Forecasted'
          }
        ]
      },
      {
        sectionTitle: 'CPM',
        rows: [
          {
            keyColor: Neutral500,
            keyShape: KeyShapeOptions.LINE,
            label: 'Historical'
          },
          {
            keyColor: Brand100,
            keyShape: KeyShapeOptions.DASHED_LINE,
            label: 'Forecasted'
          }
        ]
      }
    ]}
  />
);

interface ComparisonLegendBuilderInput {
  visibleBudgets: number[];
  primaryBudget: number;
  colorMap: Record<string, string>;
  title: string;
  includeCiIfApplicable?: boolean;
  hoveredBudget: number | undefined;
  setHoveredBudget: Dispatch<SetStateAction<number | undefined>>;
  selectedBudget: number | undefined;
  setSelectedBudget: Dispatch<SetStateAction<number | undefined>>;
}

export const buildComparisonLegend = ({
  visibleBudgets,
  primaryBudget,
  colorMap,
  title,
  includeCiIfApplicable = false,
  hoveredBudget,
  setHoveredBudget,
  selectedBudget,
  setSelectedBudget
}: ComparisonLegendBuilderInput): ReactNode => {
  const comparisonRows = visibleBudgets.map(budget => {
    const isShadowed = hoveredBudget !== undefined && hoveredBudget !== budget;
    const keyColor = isShadowed ? Neutral400 : colorMap[budget];
    const isPrimary = budget === primaryBudget;
    const formattedBudget = formatRawBudget(budget);
    const annotatedLabel = isPrimary ? `${formattedBudget} (primary)` : String(formattedBudget);

    return {
      keyColor,
      keyShape: KeyShapeOptions.DASHED_LINE,
      label: annotatedLabel,
      budget
    };
  });

  return (
    <ForecastResultsLegend
      sections={[
        {
          sectionTitle: title,
          rows: comparisonRows
        }
      ]}
      hoveredBudget={hoveredBudget}
      setHoveredBudget={setHoveredBudget}
      selectedBudget={selectedBudget}
      setSelectedBudget={setSelectedBudget}
      includeCiIfApplicable={includeCiIfApplicable}
    />
  );
};
